import React, { useEffect, useState } from 'react';
import { Check, Copy, Gavel, LogOut, Lock, LucideIcon, MinusCircle, ThumbsUp, Unlock } from 'lucide-react';
import dayjs from 'dayjs';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { cn } from '@/lib/utils';
import siteUrl from '../../../../const/const';
import {
  APPEAL_KIND_LABELS,
  Appeal,
  AppealHandout,
  AppealQuestion,
  AppealRole,
  AppealRoom,
} from '../../../../services/appealsService';

// ─── Shared class names ───────────────────────────────────────────────────────

export const cardCls = 'bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm';

export const fieldCls =
  'block w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-50 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-blue-500 focus:bg-white dark:focus:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 transition-colors';

export const labelCls = 'block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5';

export const primaryBtnCls =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';

export const secondaryBtnCls =
  'inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed';

export const ROLE_LABELS: Record<AppealRole, string> = {
  owner: 'Təşkilatçı',
  team: 'Komanda',
  jury: 'Jüri',
};

const ROLE_BADGE: Record<AppealRole, string> = {
  owner: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-indigo-300',
  team: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  jury: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
};

export const formatTime = (iso: string) => dayjs(iso).format('DD.MM HH:mm');

// ─── Room header ──────────────────────────────────────────────────────────────

export const RoomHeader: React.FC<{
  room: AppealRoom;
  role: AppealRole;
  name: string;
  onLeave: () => void;
  actions?: React.ReactNode;
}> = ({ room, role, name, onLeave, actions }) => (
  <div className={cn(cardCls, 'px-5 py-4 sm:px-6 sm:py-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between')}>
    <div className="flex min-w-0 items-center gap-3">
      <div className="w-10 h-10 shrink-0 rounded-xl bg-rose-600 flex items-center justify-center text-white">
        <Gavel className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <h1 className="text-left tracking-normal text-lg sm:text-xl font-black text-gray-900 dark:text-gray-50 leading-tight truncate">
          {room.name}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <span className={cn('rounded-full px-2 py-0.5 font-semibold', ROLE_BADGE[role])}>
            {ROLE_LABELS[role]}{name ? `: ${name}` : ''}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold',
              room.isOpen
                ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
            )}
          >
            {room.isOpen ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
            {room.isOpen ? 'Açıq' : 'Bağlı'}
          </span>
        </div>
      </div>
    </div>
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {actions}
      <button type="button" onClick={onLeave} className={secondaryBtnCls}>
        <LogOut className="w-4 h-4" />
        Çıx
      </button>
    </div>
  </div>
);

// ─── Code card ────────────────────────────────────────────────────────────────

export const CodeCard: React.FC<{ label: string; hint: string; code: string; tone: 'team' | 'jury' | 'owner' }> = ({
  label,
  hint,
  code,
  tone,
}) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard
      ?.writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  return (
    <div
      className={cn(
        'rounded-2xl border p-4',
        tone === 'team' && 'border-emerald-100 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/40',
        tone === 'jury' && 'border-amber-100 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/40',
        tone === 'owner' && 'border-blue-100 bg-blue-50/60 dark:border-blue-900 dark:bg-blue-950/40'
      )}
    >
      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="font-mono text-2xl font-black tracking-[0.2em] text-gray-900 dark:text-gray-50">{code}</span>
        <button
          type="button"
          onClick={copy}
          aria-label={`${label} kopyala`}
          className="shrink-0 rounded-lg p-2 text-gray-500 dark:text-gray-400 transition-colors hover:bg-white/80 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
    </div>
  );
};

// ─── Appeal text ──────────────────────────────────────────────────────────────

export const richTextCls =
  'text-sm leading-relaxed text-gray-800 dark:text-gray-200 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_ul]:pl-5 [&_ol]:pl-5 [&_ul_li]:list-disc [&_ol_li]:list-decimal [&_li]:my-0.5 [&_li_p]:my-0 [&_li]:marker:text-gray-400 [&_h2]:text-base [&_h2]:font-bold [&_h3]:font-bold [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_a]:text-blue-600 dark:[&_a]:text-indigo-300 [&_a]:underline [&_a]:break-all [&_code]:rounded [&_code]:bg-gray-100 dark:[&_code]:bg-gray-800 [&_code]:px-1';

/**
 * Shows an appeal's rich text. The server already sanitizes it; rendering through a read-only
 * editor additionally drops anything outside the editor's schema (scripts, attributes, unsafe links).
 */
export const RichTextView: React.FC<{ html: string; className?: string }> = ({ html, className }) => {
  const editor = useEditor({
    editable: false,
    extensions: [StarterKit.configure({ trailingNode: false, link: { openOnClick: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer nofollow' } } })],
    content: html,
  });

  useEffect(() => {
    if (editor && editor.getHTML() !== html) editor.commands.setContent(html, { emitUpdate: false });
  }, [editor, html]);

  return (
    <EditorContent
      editor={editor}
      className={cn(richTextCls, '[&_.ProseMirror]:outline-none [&_.ProseMirror>*:first-child]:mt-0 [&_.ProseMirror>*:last-child]:mb-0', className)}
    />
  );
};

const KIND_BADGE = {
  removeQuestion: { icon: MinusCircle, cls: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300' },
  acceptAnswer: { icon: ThumbsUp, cls: 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300' },
} as const;

/** What the team asked for, plus its answer when it asks for the answer to be counted. */
export const AppealRequest: React.FC<{ appeal: Pick<Appeal, 'kind' | 'teamAnswer'> }> = ({ appeal }) => {
  const { icon: Icon, cls } = KIND_BADGE[appeal.kind];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', cls)}>
        <Icon className="w-3.5 h-3.5" />
        {APPEAL_KIND_LABELS[appeal.kind]}
      </span>
      {appeal.kind === 'acceptAnswer' && appeal.teamAnswer && (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Komandanın cavabı:</span> {appeal.teamAnswer}
        </span>
      )}
    </div>
  );
};

// ─── Question display ─────────────────────────────────────────────────────────

const KIND_LABELS = { single: '', duplet: 'Duplet', blitz: 'Blits' } as const;

const chipCls =
  'rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:text-gray-300';

export const eyebrowCls = 'text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500';

const isUrl = (s: string) => /^https?:\/\/\S+$/i.test(s);

const Meta: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid gap-0.5 sm:grid-cols-[8.5rem_1fr] sm:gap-3">
    <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 sm:pt-0.5">{label}</dt>
    <dd className="min-w-0 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line break-words">{children}</dd>
  </div>
);

const HandoutView: React.FC<{ handout: AppealHandout | null }> = ({ handout }) => {
  if (!handout) return null;
  return (
    <figure className="space-y-1.5">
      <figcaption className={eyebrowCls}>Paylama materialı</figcaption>
      {handout.kind === 'text' ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 px-4 py-3 text-sm text-gray-800 dark:text-gray-100 whitespace-pre-line">
          {handout.text}
        </div>
      ) : (
        <img
          src={`${siteUrl}images/${handout.url}`}
          alt="Paylama materialı"
          className="max-h-80 max-w-full rounded-xl border border-gray-100 dark:border-gray-800"
        />
      )}
    </figure>
  );
};

/** Full question: handouts, text and answer for every part, plus the comment and sources. */
export const QuestionView: React.FC<{ question: AppealQuestion; showAnswer?: boolean }> = ({
  question,
  showAnswer = true,
}) => {
  const multi = question.parts.length > 1;
  const hasMeta =
    showAnswer && Boolean(question.considered || question.comment || question.sources.length || question.authors);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-lg font-black text-gray-900 dark:text-gray-50">Sual {question.number}</span>
        {KIND_LABELS[question.kind] && <span className={chipCls}>{KIND_LABELS[question.kind]}</span>}
        {question.isTranslated && <span className={chipCls}>Tərcümə</span>}
      </div>

      {question.noteBefore && (
        <p className="border-l-2 border-gray-200 dark:border-gray-700 pl-3 text-sm italic text-gray-600 dark:text-gray-400 whitespace-pre-line">
          {question.noteBefore}
        </p>
      )}

      <HandoutView handout={question.sharedHandout} />
      <div className="space-y-4">
        {question.parts.map((part, i) => (
          <div key={i} className="space-y-2">
            <HandoutView handout={part.handout} />
            <p className="text-[15px] leading-relaxed text-gray-900 dark:text-gray-100 whitespace-pre-line">
              {multi && <span className="font-bold">{i + 1}. </span>}
              {part.text}
            </p>
            {showAnswer && part.answer && (
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/50 px-3.5 py-2.5">
                <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">Cavab</p>
                <p className="text-sm font-bold text-emerald-950 dark:text-emerald-100 whitespace-pre-line">{part.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {hasMeta && (
        <dl className="space-y-2 border-t border-gray-100 dark:border-gray-800 pt-3">
          {question.considered && <Meta label="Sayılma meyarı">{question.considered}</Meta>}
          {question.comment && <Meta label="Şərh">{question.comment}</Meta>}
          {question.sources.length > 0 && (
            <Meta label="Mənbə">
              <ol className="space-y-0.5">
                {question.sources.map((src, i) => (
                  <li key={i} className="break-all">
                    {isUrl(src) ? (
                      <a href={src} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-600 dark:text-indigo-300 hover:underline">
                        {src}
                      </a>
                    ) : (
                      src
                    )}
                  </li>
                ))}
              </ol>
            </Meta>
          )}
          {question.authors && <Meta label="Müəllif">{question.authors}</Meta>}
        </dl>
      )}
    </div>
  );
};

// ─── Layout helpers ───────────────────────────────────────────────────────────

/** Segmented tab bar: a quiet track with the active tab raised, like the form's switches. */
export const Tabs = <T extends string>({
  value,
  onChange,
  tabs,
}: {
  value: T;
  onChange: (value: T) => void;
  tabs: { key: T; label: string; count?: number; icon?: LucideIcon; dot?: string }[];
}) => (
  <div role="tablist" className="flex gap-1 overflow-x-auto rounded-xl bg-gray-200/60 dark:bg-gray-900 p-1">
    {tabs.map(({ key, label, count, icon: Icon, dot }) => {
      const active = value === key;
      return (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(key)}
          className={cn(
            'flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
            active
              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
          )}
        >
          {Icon && <Icon className="w-4 h-4" />}
          {label}
          {count !== undefined && (
            <span
              className={cn(
                'min-w-5 rounded-full px-1.5 text-xs tabular-nums',
                active ? 'bg-blue-600 text-white' : 'bg-gray-300/60 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
              )}
            >
              {count}
            </span>
          )}
          {dot && <span className="h-2 w-2 rounded-full bg-rose-500" aria-label={dot} />}
        </button>
      );
    })}
  </div>
);

/** Banner for a closed room. */
export const ClosedNotice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-3 rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm font-medium text-amber-800 dark:text-amber-200">
    <Lock className="w-4 h-4 shrink-0" />
    {children}
  </div>
);

export const ErrorText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-950/50 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300">
    {children}
  </p>
);

export const EmptyState: React.FC<{ icon?: LucideIcon; children: React.ReactNode }> = ({ icon: Icon = Gavel, children }) => (
  <div className={cn(cardCls, 'flex flex-col items-center gap-3 px-6 py-14 text-center')}>
    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500">
      <Icon className="w-6 h-6" />
    </span>
    <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">{children}</p>
  </div>
);
